import { DataBlockDto, VisitDto } from '@lmt/shared-types';
import React, { useMemo } from 'react';
import {
  CaseItem,
  CaseItemType,
  SubCaseItemType,
  VisitWithDoctor,
} from '../../types';
import { DataBlockPreview } from './DataBlockPreview';
import { VisitPreview } from './VisitPreview';
import { useInView } from 'react-intersection-observer';
import { Freeze } from 'react-freeze';

import styles from './CaseBlocks.module.scss';

interface CaseContentsProps {
  patientId: number;
  caseId: number;
  caseItems: CaseItem[];
  changeCurrentItem: (itemId: string) => void;
  isReadOnly?: boolean;
}

type BlockWrapperProps = {
  patientId: number;
  caseDataBlockId: string;
  isParentBlock?: boolean;
  isFirstBlock?: boolean;
  item?: DataBlockDto;
  isGDTEnabled?: boolean;
  isHL7Enabled?: boolean;
  isNotDeletable?: boolean;
  showStatistic?: boolean;
  isStatisticCompleted?: boolean;
  isReadOnly?: boolean;
  // If set, enables freezing when outside boundingParent
  boundingParent?: React.RefObject<Element>;
};

const SmartFreeze: React.FC<
  React.PropsWithChildren<{
    boundingParent?: React.RefObject<Element>;
  }>
> = (props) => {
  const { boundingParent } = props;

  const { ref, inView, entry } = useInView({
    initialInView: true,
    root: boundingParent?.current,
  });

  const placeholder = useMemo(
    () =>
      entry?.boundingClientRect.height ? (
        <div
          style={{
            height: entry?.boundingClientRect.height,
          }}
        />
      ) : null,
    [entry?.boundingClientRect.height],
  );

  return (
    <div ref={ref}>
      <Freeze freeze={!!placeholder && !inView} placeholder={placeholder}>
        {props.children}
      </Freeze>
    </div>
  );
};

const BlockWrapper: React.FC<React.PropsWithChildren<BlockWrapperProps>> = (
  props,
) => {
  const {
    caseDataBlockId,
    isParentBlock = false,
    isFirstBlock,
    item,
    isGDTEnabled,
    isHL7Enabled,
    isNotDeletable,
    patientId,
    boundingParent,
    showStatistic,
    isStatisticCompleted,
    isReadOnly,
  } = props;

  const content = item ? (
    <DataBlockPreview
      type="update_data_block"
      patientId={patientId}
      dataBlock={item}
      isGDTEnabled={isGDTEnabled ?? false}
      isHL7Enabled={isHL7Enabled ?? false}
      isNotDeletable={isNotDeletable ?? false}
      isFirstBlock={isFirstBlock}
      showStatistic={showStatistic}
      isStatisticCompleted={isStatisticCompleted}
      isReadOnly={isReadOnly}
    />
  ) : (
    props.children
  );

  return (
    <div
      key={`${caseDataBlockId}`}
      id={`caseItem${caseDataBlockId}`}
      data-first-block={isFirstBlock}
      className={isParentBlock ? styles.block : styles.subitem}
    >
      <div
        data-case-item-id={caseDataBlockId}
        className={styles.blockScrollMarker}
        data-item={!!item}
      />
      {!!boundingParent ? (
        <SmartFreeze boundingParent={boundingParent}>{content}</SmartFreeze>
      ) : (
        content
      )}
    </div>
  );
};

export const CaseBlocks: React.FC<CaseContentsProps> = React.memo(
  function CaseBlocks(props) {
    const { caseItems } = props;
    const mainContainerRef = React.useRef<HTMLDivElement>(null);
    return (
      <div ref={mainContainerRef} className={styles.mainContainer}>
        <div className={styles.container}>
          {caseItems.map((caseItem) => {
            switch (caseItem.type) {
              case CaseItemType.caseData:
                return (
                  <BlockWrapper
                    key={caseItem.id}
                    patientId={props.patientId}
                    isParentBlock={true}
                    caseDataBlockId={caseItem.subitems[0]?.id ?? caseItem.id}
                    isNotDeletable={caseItem.subitems[0]?.isNotDeletable}
                  >
                    {caseItem.subitems.map((caseDataBlock) => (
                      <BlockWrapper
                        patientId={props.patientId}
                        key={caseDataBlock.id}
                        isFirstBlock={
                          caseDataBlock.id === caseItem.subitems[0].id
                        }
                        item={caseDataBlock.item}
                        caseDataBlockId={caseDataBlock.id}
                        isNotDeletable={caseDataBlock.isNotDeletable}
                        boundingParent={mainContainerRef}
                        showStatistic={caseDataBlock.showStatistic}
                        isGDTEnabled={caseDataBlock.isGDTEnabled}
                        isHL7Enabled={caseDataBlock.isHL7Enabled}
                        isStatisticCompleted={
                          caseDataBlock.isStatisticCompleted
                        }
                        isReadOnly={props.isReadOnly}
                      />
                    ))}
                  </BlockWrapper>
                );
              case CaseItemType.visit:
                return (
                  <BlockWrapper
                    key={caseItem.id}
                    patientId={props.patientId}
                    isParentBlock={true}
                    caseDataBlockId={caseItem.id}
                    isReadOnly={props.isReadOnly}
                  >
                    <SmartFreeze boundingParent={mainContainerRef}>
                      <VisitPreview
                        visit={caseItem.item as VisitWithDoctor}
                        dataBlockId={
                          caseItem.subitems.find(
                            (item) =>
                              item.type === SubCaseItemType.visitPreview,
                          )?.item.id
                        }
                        isReadOnly={props.isReadOnly}
                      />
                    </SmartFreeze>

                    {caseItem.subitems
                      .filter(
                        (item) => item.type !== SubCaseItemType.visitPreview,
                      )
                      .map((caseDataBlock) => (
                        <BlockWrapper
                          key={caseDataBlock.id}
                          patientId={props.patientId}
                          item={caseDataBlock.item}
                          caseDataBlockId={caseDataBlock.id}
                          isGDTEnabled={caseDataBlock.isGDTEnabled}
                          isHL7Enabled={caseDataBlock.isHL7Enabled}
                          boundingParent={mainContainerRef}
                          showStatistic={caseDataBlock.showStatistic}
                          isStatisticCompleted={
                            caseDataBlock.isStatisticCompleted
                          }
                          isReadOnly={props.isReadOnly}
                        />
                      ))}
                  </BlockWrapper>
                );
              case CaseItemType.measurement:
                return (
                  <BlockWrapper
                    key={caseItem.id}
                    patientId={props.patientId}
                    isParentBlock={true}
                    item={caseItem.item}
                    caseDataBlockId={caseItem.id}
                    isGDTEnabled={caseItem.isGDTEnabled}
                    isHL7Enabled={caseItem.isHL7Enabled}
                    boundingParent={mainContainerRef}
                    showStatistic={caseItem.showStatistic}
                    isStatisticCompleted={caseItem.isStatisticCompleted}
                    isReadOnly={props.isReadOnly}
                  />
                );
            }
          })}
        </div>
      </div>
    );
  },
);
